import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalUi from "../../primitives/ModalUi";

const DEFAULT_THRESHOLD = 85;
const MIN_THRESHOLD = 50;
const MAX_THRESHOLD = 100;

function IdentityRequirementModal({
  isOpen,
  onClose,
  onSave,
  signer,
  initialConfig
}) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(initialConfig?.enabled ?? false);
  const [threshold, setThreshold] = useState(
    initialConfig?.threshold || DEFAULT_THRESHOLD
  );
  const [notes, setNotes] = useState(initialConfig?.notes || "");

  useEffect(() => {
    if (isOpen) {
      setEnabled(initialConfig?.enabled ?? false);
      setThreshold(initialConfig?.threshold || DEFAULT_THRESHOLD);
      setNotes(initialConfig?.notes || "");
    }
  }, [isOpen, initialConfig]);

  const handleSave = () => {
    if (!enabled) {
      onSave?.(null);
      return;
    }
    const numericThreshold = Number(threshold);
    const safeThreshold = Number.isNaN(numericThreshold)
      ? DEFAULT_THRESHOLD
      : Math.min(MAX_THRESHOLD, Math.max(MIN_THRESHOLD, numericThreshold));
    onSave?.({
      enabled: true,
      threshold: safeThreshold,
      notes: notes?.trim() || "",
      provider: initialConfig?.provider || "rekognition"
    });
  };

  return (
    <ModalUi
      title={t("identity-settings-title", {
        name: signer?.Name || signer?.Role || ""
      })}
      isOpen={isOpen}
      handleClose={onClose}
    >
      <div className="flex flex-col gap-4 text-sm">
        <p className="text-base-content/70">
          {t("identity-settings-description")}
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="op-checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>{t("identity-settings-enable")}</span>
        </label>
        <fieldset
          className={`flex flex-col gap-3 rounded-xl border border-base-300 p-4 ${
            !enabled ? "opacity-60 pointer-events-none" : ""
          }`}
        >
        <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-base-content/70">
              {t("identity-settings-threshold")}
            </span>
            <input
              type="number"
              min={MIN_THRESHOLD}
              max={MAX_THRESHOLD}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="op-input op-input-bordered op-input-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-base-content/70">
              {t("identity-settings-notes")}
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="op-textarea op-textarea-bordered text-sm"
              placeholder={t("identity-settings-notes-placeholder")}
            />
          </label>
        </fieldset>
        <div className="flex flex-col md:flex-row justify-end gap-3">
          <button
            type="button"
            className="op-btn op-btn-ghost"
            onClick={onClose}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className="op-btn op-btn-primary"
            onClick={handleSave}
          >
            {enabled
              ? t("identity-settings-save")
              : t("identity-settings-disable")}
          </button>
        </div>
      </div>
    </ModalUi>
  );
}

export default IdentityRequirementModal;
